/**
 * 日付処理ユーティリティ
 */

/**
 * Firestore 取得時の型ゆれを吸収して Date に統一する（例外を投げない）
 * - Timestamp（.toDate あり）→ toDate()
 * - 既に Date → そのまま
 * - 数値(ms) → new Date(value)
 * - 上記以外・null・undefined → new Date(0)
 */
export function toDate(value: unknown): Date {
  if (value instanceof Date) return value
  if (typeof value === 'number') return new Date(value)
  const t = value as { toDate?: () => Date }
  if (typeof t?.toDate === 'function') return t.toDate()
  return new Date(0)
}

/**
 * JSONからDateオブジェクトに変換（必須）
 * @param value - Firestore Timestamp または Date文字列、または {__time__: string} 形式
 * @returns Dateオブジェクト
 */
export function parseDate(value: any): Date {
  if (!value) {
    throw new Error('Date value is required');
  }
  
  // Firestore Timestampの場合
  if (value && typeof value.toDate === 'function') {
    return value.toDate();
  }
  
  // {__time__: string} 形式の場合（Firestoreのシリアライズ形式）
  if (value && typeof value === 'object' && value.__time__) {
    const date = new Date(value.__time__);
    if (isNaN(date.getTime())) {
      throw new Error(`Invalid date string: ${value.__time__}`);
    }
    return date;
  }
  
  // Date文字列の場合
  if (typeof value === 'string') {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      throw new Error(`Invalid date string: ${value}`);
    }
    return date;
  }
  
  // 既にDateオブジェクトの場合
  if (value instanceof Date) {
    return value;
  }
  
  throw new Error(`Cannot parse date from value: ${value}`);
}

/**
 * JSONからDateオブジェクトに変換（オプショナル）
 * @param value - Firestore Timestamp または Date文字列、または {__time__: string} 形式
 * @returns Dateオブジェクトまたはundefined
 */
export function parseDateOptional(value: any): Date | undefined {
  if (!value) {
    return undefined;
  }
  
  try {
    return parseDate(value);
  } catch {
    return undefined;
  }
}

/**
 * DateオブジェクトをJSON形式（ISO文字列）に変換
 * @param date - Dateオブジェクト
 * @returns ISO文字列またはnull
 */
export function dateToJson(date: Date | undefined | null): string | null {
  if (!date) {
    return null;
  }
  return date.toISOString();
}

const JA_WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'] as const

/** Date を input[type=date] 用の YYYY-MM-DD へ整形 */
export function formatDateInputValue(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** 今日の日付を input[type=date] 用の YYYY-MM-DD で返す */
export function getTodayDateInputValue(): string {
  return formatDateInputValue(new Date())
}

/**
 * 事業所回答フォームの回答期限が「まだ期限内」か（操作者 PC のローカル日付で判定）。
 * DB の deadline は date 入力由来で UTC 同日 0:00 の瞬間になりやすく、そのまま `now < deadline` だと
 * 日本時間では期限日 9:00 以降が誤って期限切れになる。
 * town 回答画面と同様、ローカル暦での期限日その日の 23:59:59.999 までを期限内とする
 * （翌日 0:00 になった時点で期限切れに切り替わる）。
 */
export function isWithinSurveyFormDeadlineCalendarDay(deadline: Date, now: Date = new Date()): boolean {
  const deadlineEnd = new Date(deadline)
  deadlineEnd.setHours(23, 59, 59, 999)
  return now <= deadlineEnd
}

/** Date を YYYY年M月D日(曜) へ整形 */
export function formatDateJaWithWeekday(date: Date): string {
  return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日(${JA_WEEKDAYS[date.getDay()]})`
}

/** Date を M/D(曜) へ整形 */
export function formatMonthDayJaWithWeekday(date: Date): string {
  return `${date.getMonth() + 1}/${date.getDate()}(${JA_WEEKDAYS[date.getDay()]})`
}

/** Date を YYYY/MM/DD へ整形 */
export function formatDateSlashYmd(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}/${m}/${d}`
}

/** 日付文字列を M/D(曜) へ整形（不正値は原文を返す） */
export function formatDateStringToMonthDayJaWithWeekday(dateString: string): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return dateString
  return formatMonthDayJaWithWeekday(date)
}

/**
 * ローカル日付を日本語ロケールで表示（ブラウザ既定の短い日付形式、例: 2026/4/1）
 */
export function formatLocaleDateJa(date: Date | null | undefined): string {
  if (date == null) return ''
  return date.toLocaleDateString('ja-JP')
}

/** Date を日本語ロケールで日時表示（例: 2026/04/01 09:30） */
export function formatLocaleDateTimeJa(date: Date | null | undefined): string {
  if (date == null) return ''
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

